import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { getPlatformSession } from "@/lib/api";
import { signOutCurrentSession } from "@/lib/authSession";
import { useAuthStore } from "@/stores/authStore";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RouteFallback } from "@/components/RouteFallback";
import { Toaster } from "@/components/Toaster";
import { StepUpDialog } from "@/components/StepUpDialog";
import { useRealtimeInvalidation } from "@/hooks/useRealtimeInvalidation";

// Route-level code splitting (#1472): every routed page is loaded as its own
// lazy chunk via a dynamic import() so first load ships only the app shell +
// vendor, not all ~24 pages at once. Pages are named exports, so map each to a
// `default` for React.lazy. Mirrors apps/web/src/App.tsx; the <Suspense>
// boundary (with the token-styled RouteFallback) lives below, inside the
// top-level <ErrorBoundary> so a failed chunk fetch lands on the graceful
// fallback rather than white-screening.
const DashboardPage = lazy(() => import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const LoginPage = lazy(() => import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const DriverRequestsPage = lazy(() => import("@/pages/DriverRequestsPage").then((m) => ({ default: m.DriverRequestsPage })));
const BusinessRequestsPage = lazy(() => import("@/pages/BusinessRequestsPage").then((m) => ({ default: m.BusinessRequestsPage })));
const InquiriesPage = lazy(() => import("@/pages/InquiriesPage").then((m) => ({ default: m.InquiriesPage })));
const UsersPage = lazy(() => import("@/pages/UsersPage").then((m) => ({ default: m.UsersPage })));
const UserDetailPage = lazy(() => import("@/pages/UserDetailPage").then((m) => ({ default: m.UserDetailPage })));
const DriversPage = lazy(() => import("@/pages/DriversPage").then((m) => ({ default: m.DriversPage })));
const DriverDetailPage = lazy(() => import("@/pages/DriverDetailPage").then((m) => ({ default: m.DriverDetailPage })));
const BusinessesPage = lazy(() => import("@/pages/BusinessesPage").then((m) => ({ default: m.BusinessesPage })));
const BusinessDetailPage = lazy(() => import("@/pages/BusinessDetailPage").then((m) => ({ default: m.BusinessDetailPage })));
const VehiclesPage = lazy(() => import("@/pages/VehiclesPage").then((m) => ({ default: m.VehiclesPage })));
const ReservationsPage = lazy(() => import("@/pages/ReservationsPage").then((m) => ({ default: m.ReservationsPage })));
const ReservationDetailPage = lazy(() => import("@/pages/ReservationDetailPage").then((m) => ({ default: m.ReservationDetailPage })));
const MessagingPage = lazy(() => import("@/pages/MessagingPage").then((m) => ({ default: m.MessagingPage })));
const SmsConsentPage = lazy(() => import("@/pages/SmsConsentPage").then((m) => ({ default: m.SmsConsentPage })));
const SupportPage = lazy(() => import("@/pages/SupportPage").then((m) => ({ default: m.SupportPage })));
const TiersPage = lazy(() => import("@/pages/TiersPage").then((m) => ({ default: m.TiersPage })));
const AuditLogPage = lazy(() => import("@/pages/AuditLogPage").then((m) => ({ default: m.AuditLogPage })));
const ComplimentaryGrantsPage = lazy(() => import("@/pages/ComplimentaryGrantsPage").then((m) => ({ default: m.ComplimentaryGrantsPage })));
const AdminsPage = lazy(() => import("@/pages/AdminsPage").then((m) => ({ default: m.AdminsPage })));
const TestAccountsPage = lazy(() => import("@/pages/TestAccountsPage").then((m) => ({ default: m.TestAccountsPage })));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

/**
 * Initializes authentication state on app load.
 *
 * Uses Supabase's INITIAL_SESSION event which fires once when the client
 * has finished loading the session from storage. This handles:
 * 1. No localStorage auth but valid Supabase session - verify via API
 * 2. No auth at all - mark as not loading
 * 3. Auth state changes (sign out, token refresh)
 * @returns Null component (side-effect only)
 */
function AuthInitializer(): null {
  const { setAdmin, clearAuth } = useAuthStore();

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "INITIAL_SESSION") {
        if (session) {
          // A persisted admin record is only a hint. Keep protected routes in
          // loading until the Supabase session is present and the API confirms
          // the user is still a platform admin.
          try {
            const platformAdmin = await getPlatformSession();
            setAdmin(platformAdmin);
          } catch {
            await signOutCurrentSession();
            clearAuth();
          }
        } else {
          // No session - not authenticated
          clearAuth();
        }
      } else if (event === "SIGNED_OUT") {
        clearAuth();
      } else if (event === "TOKEN_REFRESHED" && session) {
        // Re-validate admin status on token refresh
        try {
          const platformAdmin = await getPlatformSession();
          setAdmin(platformAdmin);
        } catch {
          await signOutCurrentSession();
          clearAuth();
        }
      }
      // SIGNED_IN: Handled by LoginPage to avoid race conditions
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [setAdmin, clearAuth]);

  return null;
}

/**
 * Headless bridge: subscribes to the admin's realtime channel + invalidates caches.
 * @returns Nothing (renders null).
 */
function RealtimeBridge(): null {
  useRealtimeInvalidation();
  return null;
}

/**
 * Admin application component.
 * @returns React element for the admin app
 */
export function App(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthInitializer />
        <RealtimeBridge />
        {/* App-level toast outlet (#562) — raise via `toast` from
            @/stores/toastStore in mutation onSuccess/onError handlers. */}
        <Toaster />
        {/* Step-up re-auth challenge dialog (#1402) — opened by api.ts's
            response interceptor on a 428 STEP_UP_REQUIRED from a
            "recency"/"session"-gated platform-admin route. */}
        <StepUpDialog />
        {/* Route shell boundary: a render error in any page — or a failed
            lazy-chunk fetch (#1472) — falls back to a graceful card instead of
            white-screening the whole admin app (#295). Suspense sits INSIDE the
            boundary so a rejected dynamic import() is caught here too. */}
        <ErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Auth routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/driver-requests"
            element={
              <ProtectedRoute>
                <DriverRequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/company-requests"
            element={
              <ProtectedRoute>
                <BusinessRequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inquiries"
            element={
              <ProtectedRoute>
                <InquiriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:id"
            element={
              <ProtectedRoute>
                <UserDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drivers"
            element={
              <ProtectedRoute>
                <DriversPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drivers/:id"
            element={
              <ProtectedRoute>
                <DriverDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies"
            element={
              <ProtectedRoute>
                <BusinessesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/:id"
            element={
              <ProtectedRoute>
                {/* Per-route boundary: keeps the nav/shell usable if the detail
                    page itself throws on a malformed field (#295). */}
                <ErrorBoundary>
                  <BusinessDetailPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles"
            element={
              <ProtectedRoute>
                <VehiclesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reservations"
            element={
              <ProtectedRoute>
                <ReservationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reservations/:id"
            element={
              <ProtectedRoute>
                <ReservationDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messaging"
            element={
              <ProtectedRoute>
                <MessagingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tiers"
            element={
              <ProtectedRoute>
                <TiersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-log"
            element={
              <ProtectedRoute>
                <AuditLogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sms-consent"
            element={
              <ProtectedRoute>
                <SmsConsentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/complimentary-grants"
            element={
              <ProtectedRoute>
                <ComplimentaryGrantsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admins"
            element={
              <ProtectedRoute>
                <AdminsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/test-accounts"
            element={
              <ProtectedRoute>
                <TestAccountsPage />
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
